import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiRequestError, api } from '../lib/api.js';
import { CondosMark, CondosWordmark } from '../components/brand.js';
import { Alert, Button, Field, MadeByAzkoGroup, PasswordInput } from '../components/ui.js';

/**
 * Destino do link de recuperacao.
 *
 * Rota PUBLICA, ao lado de `/retirada/:token` e pelo mesmo motivo: quem abre
 * este endereco nao tem sessao — e nao ter e exatamente o problema que ele
 * resolve. Abaixo do guard de autenticacao, o link do e-mail desembocaria na
 * tela de login, que e de onde a pessoa acabou de sair sem conseguir entrar.
 *
 * O token nunca aparece na tela e nao e conferido antes do envio: perguntar
 * "este token vale?" ao abrir a pagina daria a quem tem uma lista de tokens um
 * jeito barato de testar todos. Ele e gasto uma vez so, no envio.
 */
export function ResetPasswordPage() {
  const { token = '' } = useParams();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);

  const curta = password.length > 0 && password.length < 8;
  const diferente = confirm.length > 0 && confirm !== password;

  async function enviar(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setEnviando(true);

    try {
      await api.post('/auth/redefinir', { token, password });
      setPronto(true);
    } catch (cause) {
      setError(
        cause instanceof ApiRequestError
          ? cause.message
          : 'Nao consegui redefinir a senha. Tente de novo.',
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="safe-area-all safe-screen grid place-items-center px-5 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3.5">
          <span className="grid size-11 place-items-center rounded-2xl bg-accent text-on-accent">
            <CondosMark className="h-6 w-5.5" />
          </span>
          <CondosWordmark height="1.15rem" />
        </div>

        {pronto ? (
          <>
            <p className="eyebrow">Pronto</p>
            <h1 className="mt-2 text-3xl font-extrabold tracking-tight">Senha trocada.</h1>
            <p className="mt-2 text-sm text-muted">
              Ela ja vale, e as sessoes que estavam abertas com a senha antiga foram
              encerradas.
            </p>
            <Link to="/" className="mt-8 block">
              <Button className="w-full">Ir para o login</Button>
            </Link>
          </>
        ) : (
          <>
            <p className="eyebrow">Recuperacao</p>
            <h1 className="mt-2 text-3xl font-extrabold tracking-tight">Nova senha.</h1>
            <p className="mt-2 text-sm text-muted">
              Escolha uma senha de pelo menos 8 caracteres. Este link so funciona uma vez.
            </p>

            <form className="mt-8 space-y-5" onSubmit={enviar} noValidate>
              <Field label="Nova senha" error={curta ? 'Use pelo menos 8 caracteres.' : undefined}>
                <PasswordInput
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                  placeholder="••••••••"
                  autoFocus
                  required
                />
              </Field>

              {/* A confirmacao existe porque o campo e mascarado e o erro so
                  apareceria no proximo login — com o link ja gasto. */}
              <Field label="Repita a senha" error={diferente ? 'As duas nao batem.' : undefined}>
                <PasswordInput
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                  autoComplete="new-password"
                  placeholder="••••••••"
                  required
                />
              </Field>

              {error && <Alert>{error}</Alert>}

              <Button
                type="submit"
                className="w-full"
                loading={enviando}
                disabled={password.length < 8 || confirm !== password}
              >
                Salvar nova senha
              </Button>
            </form>
          </>
        )}

        <MadeByAzkoGroup className="mt-8" />
      </div>
    </div>
  );
}
